import {tv} from 'tailwind-variants'
import type {
  HgHeadingContentTypeProps,
  HgButtonLinkProps,
} from '~/design-system/hg/components'
import type {HgHeroCTAProps} from '..'

type GetHeadingContentTypePropsOptions = {
  props: HgHeroCTAProps
  buttonVariant?: Extract<HgButtonLinkProps['variant'], 'tonal' | 'frosted'>
  alwaysStackedEmail?: boolean
}

export function getHeadingContentTypeProps({
  props,
  buttonVariant = 'tonal',
  alwaysStackedEmail = false,
}: GetHeadingContentTypePropsOptions): HgHeadingContentTypeProps {
  switch (props.contentType) {
    case 'emailCapture': {
      if (!props.emailCaptureProps) break

      const emailCaptureProps = alwaysStackedEmail
        ? {...props.emailCaptureProps, alwaysStacked: true}
        : props.emailCaptureProps

      return {
        emailCaptureProps,
        contentType: 'emailCapture',
      }
    }
    case 'buttonGroup':
      return {
        contentType: 'buttonGroup',
        primaryButtonProps: props.primaryButtonProps,
        secondaryButtonProps: props.secondaryButtonProps
          ? {
              ...props.secondaryButtonProps,
              variant: buttonVariant,
            }
          : undefined,
      }
    case 'none':
    default:
      return {contentType: 'none'}
  }
  return {contentType: 'none'}
}

export const hgHeroVariants = tv({
  slots: {
    gridSlot:
      'z-[3] col-start-1 col-end-auto row-start-1 row-end-auto mx-auto pt-navbar-height',
    headingContainer: '',
    heading: 'z-[1] text-text-default arcadia-display-6',
  },
  variants: {
    type: {
      cinematic: {},
      default: {
        heading: '',
      },
      headingOnly: {
        heading: '',
      },
      splitImage: {
        gridSlot: 'lg:px-s5',
        heading: 'items-start justify-center text-left',
        headingContainer:
          'row-start-1 flex flex-col gap-32 self-start self-center pt-s6 md:col-start-1 lg:col-span-7 lg:col-start-1 lg:data-[sticky=true]:sticky lg:data-[sticky=true]:top-[var(--top)] lg:data-[sticky=true]:self-start',
      },
    },
    alignment: {
      left: '',
      center: '',
    },
    size: {
      long: '',
      short: '',
    },
  },
  compoundVariants: [
    {
      type: 'splitImage',
      size: 'long',
      class: {
        headingContainer: 'md:col-span-full',
      },
    },
    {
      type: 'splitImage',
      size: 'short',
      class: {
        gridSlot: 'w-full',
        headingContainer: 'xl:col-span-5 xl:col-start-3',
      },
    },
    {
      type: 'headingOnly',
      alignment: 'left',
      class: {
        gridSlot: 'w-full',
        heading: 'w-full items-start justify-center text-left',
        headingContainer:
          'col-span-full col-start-1 md:col-span-9 md:col-start-1 lg:col-start-3 xl:col-span-10 xl:col-start-3 3xl:col-span-8 3xl:col-start-3',
      },
    },
  ],
})
